import { supabase } from '../lib/supabase';
import { Award, AwardCategory } from '../types/award';

const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

export const awardService = {
  // Récupérer tous les awards
  getAllAwards: async (): Promise<Award[]> => {
    const { data: awards, error } = await supabase
      .from('awards')
      .select(`
        *,
        award_categories (
          id,
          name,
          contest:contests (
            id,
            name,
            slug
          )
        )
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return awards || [];
  },

  // Récupérer un award spécifique par son ID
  getAwardById: async (id: string): Promise<Award | null> => {
    const { data: award, error } = await supabase
      .from('awards')
      .select(`
        *,
        award_categories (
          id,
          name,
          contest:contests (
            id,
            name,
            slug
          )
        )
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return award;
  },

  // Récupérer un award spécifique par son slug
  getAwardBySlug: async (slug: string): Promise<Award | null> => {
    const { data: award, error } = await supabase
      .from('awards')
      .select(`
        *,
        award_categories (
          id,
          name,
          contest:contests (
            id,
            name,
            slug
          )
        )
      `)
      .eq('slug', slug)
      .single();

    if (error) throw error;
    return award;
  },

  // Créer un nouvel award
  createAward: async (award: Partial<Award>): Promise<Award> => {
    const slug = generateSlug(award.name || '');
    
    const { data, error } = await supabase
      .from('awards')
      .insert([{ ...award, slug }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Mettre à jour un award
  updateAward: async (id: string, award: Partial<Award>): Promise<Award> => {
    const updateData: Partial<Award> = { ...award };
    
    // On ne met plus à jour le slug automatiquement quand le nom change
    // Cela permet de conserver les URLs existantes
    
    const { data, error } = await supabase
      .from('awards')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Supprimer un award
  deleteAward: async (id: string): Promise<void> => {
    const { error } = await supabase
      .from('awards')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  // Ajouter une catégorie à un award
  addCategory: async (awardId: string, contestId: string, name: string): Promise<void> => {
    const { error } = await supabase
      .from('award_categories')
      .insert([{
        award_id: awardId,
        contest_id: contestId,
        name
      }]);

    if (error) throw error;
  },

  // Supprimer une catégorie d'un award
  removeCategory: async (categoryId: string): Promise<void> => {
    const { error } = await supabase
      .from('award_categories')
      .delete()
      .eq('id', categoryId);

    if (error) throw error;
  },

  // Mettre à jour une catégorie
  updateCategory: async (categoryId: string, name: string): Promise<void> => {
    const { error } = await supabase
      .from('award_categories')
      .update({ name })
      .eq('id', categoryId);

    if (error) throw error;
  }
};
